import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiShield,
  FiEye,
  FiUsers,
  FiMail,
  FiLink,
  FiActivity,
  FiUserPlus,
  FiCheck,
  FiLoader,
  FiAlertCircle,
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "react-toastify";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const PrivacySettings = () => {
  const { user, updateProfile, fetchUserProfile } = useUserContext();

  const [privacySettings, setPrivacySettings] = useState({
    showEmail: false,
    showInstitutionEmail: true,
    showExternalLinks: true,
    allowCollaborationRequests: true,
    showActivity: true,
  });

  const [isOpenForCollaboration, setIsOpenForCollaboration] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setPrivacySettings({
        showEmail: user.profilePrivacy?.showEmail ?? false,
        showInstitutionEmail: user.profilePrivacy?.showInstitutionEmail ?? true,
        showExternalLinks: user.profilePrivacy?.showExternalLinks ?? true,
        allowCollaborationRequests:
          user.profilePrivacy?.allowCollaborationRequests ?? true,
        showActivity: user.profilePrivacy?.showActivity ?? true,
      });
      setIsOpenForCollaboration(user.isOpenForCollaboration ?? true);
    }
  }, [user]);

  const handleToggle = (key) => {
    setPrivacySettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  const handleCollaborationToggle = () => {
    setIsOpenForCollaboration((prev) => !prev);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      await updateProfile({
        profilePrivacy: privacySettings,
        isOpenForCollaboration: isOpenForCollaboration,
      });

      await fetchUserProfile();
      setHasChanges(false);
      toast.success("Privacy settings updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update privacy settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const privacyOptions = [
    {
      key: "showEmail",
      title: "Show Email Address",
      description: "Allow others to see your email address on your profile",
      icon: <FiMail />,
    },
    {
      key: "showInstitutionEmail",
      title: "Show Institution Email",
      description: "Display your institutional email for verification purposes",
      icon: <FiMail />,
    },
    {
      key: "showExternalLinks",
      title: "Show External Links",
      description: "Make your social and research links visible to others",
      icon: <FiLink />,
    },
    {
      key: "allowCollaborationRequests",
      title: "Allow Collaboration Requests",
      description: "Let other researchers send you collaboration requests",
      icon: <FiUserPlus />,
    },
    {
      key: "showActivity",
      title: "Show Activity Feed",
      description: "Allow others to see your recent activity on your profile",
      icon: <FiActivity />,
    },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-green">
            <FiShield />
          </span>
          Privacy Settings
        </h1>
        <p className="stg-section-description">
          Control what information is visible on your profile and who can
          interact with you
        </p>
      </div>

      {/* Collaboration Status Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiUsers /> Collaboration Status
          </h3>
          <div className="stg-card-actions">
            <span
              className={`stg-status ${
                isOpenForCollaboration
                  ? "stg-status-success"
                  : "stg-status-warning"
              }`}
            >
              <span className="stg-status-dot"></span>
              {isOpenForCollaboration
                ? "Open for Collaboration"
                : "Not Available"}
            </span>
          </div>
        </div>
        <div className="stg-card-body">
          <div
            className="stg-toggle-wrapper"
            style={{ borderBottom: "none", paddingBottom: 0 }}
          >
            <div className="stg-toggle-info">
              <p className="stg-toggle-label">Open for Collaboration</p>
              <p className="stg-toggle-description">
                When enabled, your profile will show that you're actively
                looking for research collaborators. This will also make you
                discoverable in the collaborator search.
              </p>
            </div>
            <label className="stg-toggle">
              <input
                type="checkbox"
                checked={isOpenForCollaboration}
                onChange={handleCollaborationToggle}
              />
              <span className="stg-toggle-slider"></span>
            </label>
          </div>
        </div>
      </motion.div>

      {/* Profile Visibility Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiEye /> Profile Visibility
          </h3>
        </div>
        <div className="stg-card-body">
          {privacyOptions.map((option, index) => (
            <div key={option.key} className="stg-toggle-wrapper">
              <div className="stg-toggle-info">
                <p
                  className="stg-toggle-label"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {option.icon} {option.title}
                </p>
                <p className="stg-toggle-description">{option.description}</p>
              </div>
              <label className="stg-toggle">
                <input
                  type="checkbox"
                  checked={privacySettings[option.key]}
                  onChange={() => handleToggle(option.key)}
                />
                <span className="stg-toggle-slider"></span>
              </label>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Privacy Info Card */}
      <motion.div variants={itemVariants}>
        <div className="stg-alert stg-alert-info">
          <FiAlertCircle className="stg-alert-icon" />
          <div className="stg-alert-content">
            <p className="stg-alert-title">About Privacy on ResearchPro</p>
            <p className="stg-alert-message">
              Your privacy settings control what information is visible to other
              users. Some basic information like your name and profile photo is
              always visible to help others identify you for potential
              collaborations. You can also remove followers from your profile at
              any time.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Save Button */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="stg-card-footer"
          style={{
            background: "transparent",
            border: "none",
            padding: "0",
            marginTop: "8px",
          }}
        >
          <button
            className="stg-btn stg-btn-primary stg-btn-lg"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FiLoader className="stg-spin" /> Saving...
              </>
            ) : (
              <>
                <FiCheck /> Save Changes
              </>
            )}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PrivacySettings;
